import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PayrollEngine, type PayrollPeriod } from '@/utils/payrollEngine';
import { useToast } from '@/hooks/use-toast';

export function usePayrollPeriods() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: periods, isLoading } = useQuery({
    queryKey: ['payroll-periods'],
    queryFn: PayrollEngine.getPayrollPeriods,
  });

  const createPeriodMutation = useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      PayrollEngine.createPayrollPeriod(year, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-periods'] });
      toast({
        title: 'Payroll Period Created',
        description: 'New payroll period has been created successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Creation Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const getMonthName = (month: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1] || '';
  };

  const getStatusColor = (status: PayrollPeriod['status']): string => {
    switch (status) {
      case 'draft': return 'text-muted-foreground';
      case 'calculated': return 'text-blue-600';
      case 'approved': return 'text-green-600';
      case 'processed': return 'text-gray-600';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'draft': return 'outline';
      case 'calculated': return 'default';
      case 'approved': return 'secondary';
      case 'processed': return 'secondary';
      default: return 'outline';
    }
  };

  return {
    periods,
    isLoading,
    createPeriod: createPeriodMutation.mutate,
    isCreating: createPeriodMutation.isPending,
    getMonthName,
    getStatusColor,
    getStatusBadgeVariant,
  };
}